import React from 'react';
import {
    Polyline1,
    Polyline2,
    Polyline3,
    Polyline4,
    Polyline5,
    QuoteMark,
    VerifiedTick,
    ProBadge
} from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';
import Image from '$partials/media/image';
import { Button } from '$partials/ui';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleQuote}
 */

const ModuleQuote = ({
    quote = '',
    textSize = 'H2',
    image,
    polyline,
    theme = '',
    orientation = 'Text left',
    avatar,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    previousTypename,
    // @ts-ignore — injected by dispatcher, not part of the CMS fragment
    isArticlePage,
}) => {

    const polyLineIcons = [Polyline1, Polyline2, Polyline3, Polyline4, Polyline5];
    const selectedPolyline = polyline === 'None' ? null : polyLineIcons[Number(polyline) - 1] || null;

    const collapseTopPadding = previousTypename === 'BvaModuleSingleImage';
    const topPaddingClass = collapseTopPadding ? ' pt-16' : ` pt-40${isArticlePage ? '' : ' md:pt-80'}`;

    const {
        formatURI,
        translate: t,
    } = useLocale();

    const isLeft = orientation === 'Text left';
    const isRight = orientation === 'Text right';
    const isCenter = orientation === 'Text center (no image)';

    let orientationClasses = 'items-center text-center flex-col';
    if (isLeft) { orientationClasses = 'items-center flex-col-reverse md:flex-row'; }
    if (isRight) { orientationClasses = 'flex-col-reverse md:flex-row-reverse md:justify-end'; }

    const sectionInnerClasses = [
        'overflow-hidden relative rounded',
        `theme-${theme} ${theme !== 'transparent' ? 'bg-theme-themed-surface-bg' : ''}`,
        isCenter ? 'pb-60 pt-32 md:pt-40 lg:pb-72 lg:pt-40' : 'py-16 sm:py-32 md:pt-40 md:pb-112 lg:pt-40',
    ].join(' ');

    const textColumnClasses = [
        'w-full md:w-7/12',
        !isCenter && 'pt-24 md:pt-48',
    ].filter(Boolean).join(' ');

    const quoteMarkClasses = [
        'text-theme-themed-quote-mark mb-24',
        isCenter && 'mx-auto',
    ].filter(Boolean).join(' ');

    const quoteTextClasses = [
        textSize === 'H2' ? 'type-style-2' : 'type-style-3',
        'break-words text-theme-themed-text-accent',
    ].filter(Boolean).join(' ');

    const avatarRowClasses = [
        'flex items-center gap-16',
        isCenter ? 'justify-center mt-56' : 'mt-32 md:mt-80',
    ].join(' ');

    const imageSpacerClasses = [
        'hidden md:block',
        isLeft ? 'md:w-1/12 lg:w-2/12' : 'md:w-1/12 lg:w-1/12',
    ].join(' ');

    const imageColumnClasses = [
        'ml-auto w-4/6 sm:w-7/12 md:w-4/12 lg:w-3/12 place-self-start',
        isLeft ? 'md:ml-0' : 'md:ml-4 lg:ml-0',
    ].join(' ');

    // render
    return (
        <section
            className={`module-quote w-full relative z-10 pb-40${isArticlePage ? '' : ' md:pb-80'}${topPaddingClass}`}>
            <div className='layout-container layout-site-outer-padding w-full overflow-hidden'>

                <div className={sectionInnerClasses}>
                    <div className='relative z-10'>
                        <div className={`px-16 sm:px-32 md:px-40 flex ${orientationClasses}`}>

                            <div className={textColumnClasses}>
                                <div>
                                    <QuoteMark className={quoteMarkClasses} />
                                    {quote && (
                                        <div className={quoteTextClasses}>
                                            {quote}
                                        </div>
                                    )}
                                </div>
                                {avatar && (
                                    avatar?.link ? (
                                        <Button
                                            as='a'
                                            className={`${avatarRowClasses} group`}
                                            href={avatar.link}>
                                            {avatar?.avatarImage && (
                                                <div className='relative'>
                                                    <Image
                                                        className='object-cover h-full w-full'
                                                        containerClassName='rounded-full h-[56px] w-[56px] overflow-hidden relative'
                                                        src={avatar?.avatarImage?.src}/>
                                                    <div
                                                        className='absolute inset-0 h-[56px] w-[56px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity'
                                                        style={{ background: 'rgba(0, 0, 0, 0.15)' }} />
                                                    {avatar?.verified && (
                                                        <div className='absolute top-0 -right-5'>
                                                            <VerifiedTick />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className='flex flex-col gap-y-3'>
                                                <span className='type-style-body text-text-primary group-hover:text-brand-500 transition-colors'>
                                                    {avatar?.name}
                                                    {avatar?.pro && <ProBadge className='inline-block ml-8 w-24'/>}
                                                </span>
                                                <span className='type-style-body text-text-tertiary'>{avatar?.role}</span>
                                            </div>
                                        </Button>
                                    ) : (
                                        <div className={avatarRowClasses}>
                                            {avatar?.avatarImage && (
                                                <div className='relative'>
                                                    <Image
                                                        className='object-cover h-full w-full'
                                                        containerClassName='rounded-full h-[56px] w-[56px] overflow-hidden relative'
                                                        src={avatar?.avatarImage?.src}/>
                                                    {avatar?.verified && (
                                                        <div className='absolute top-0 -right-5'>
                                                            <VerifiedTick />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className='flex flex-col gap-y-3'>
                                                <span className='type-style-body text-text-primary'>
                                                    {avatar?.name}
                                                    {avatar?.pro && <ProBadge className='inline-block ml-8 w-24'/>}
                                                </span>
                                                <span className='type-style-body text-text-tertiary'>{avatar?.role}</span>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                            {image && (
                                <>
                                    {/* spacer */}
                                    <div className={imageSpacerClasses} />

                                    {/* image */}
                                    <div className={imageColumnClasses}>
                                        <figure className='rounded overflow-hidden pointer-events-none select-none aspect-square h-full'>
                                            <Image
                                                className='h-full object-cover'
                                                containerClassName='h-full object-cover'
                                                height={'100%'}
                                                loadWidth={840}
                                                src={image?.src}
                                                width={'100%'} />
                                        </figure>
                                    </div>
                                </>
                            )}
                        </div>

                    </div>
                    {/* polyline */}
                    {selectedPolyline && (
                        <div
                            aria-hidden='true'
                            className='absolute top-0 right-0 w-[50dvw] z-[1]'>
                            {React.createElement(selectedPolyline, {
                                className: 'w-full h-auto text-theme-themed-polyline'
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ModuleQuote;
